import {Activity, DollarSign, TrendingDown, TrendingUp} from "react-feather";

import DefaultLayout from "../layouts/DefaultLayout";
import useTranslation from "../hooks/translations";
import {Card, Col, Container, Row, Spinner} from "react-bootstrap";
import MonthNavigator from "../components/MonthNavigator";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Link, NavLink} from "react-router-dom";
import {useApi} from "../contexts/ApiProvider";
import CostService from "../services/CostService";
import IncomeService from "../services/IncomeService";
import CurrencyUtils from "../utils/CurrencyUtils";
import {useDateRange} from "../contexts/DateRangeProvider";
import DatesUtils from "../utils/dates";
import StringUtils from "../utils/StringUtils";
import DoughnutChart from "../components/DoughnutChart";
import {String2HexCodeColor} from 'string-to-hex-code-color';
import CostTypeService from "../services/CostTypeService";
import IncomeTypeService from "../services/IncomeTypeService";
import CostCategoryService from "../services/CostCategoryService";
import IncomeCategoryService from "../services/IncomeCategoryService";
import ProtectedContent from "../components/ProtectedContent";
import CostsDistributionList from "../components/CostsDistributionList";
import IncomesDistributionList from "../components/IncomesDistributionList";

const defaultChartBackgroundColors = [
  "rgba(0, 97, 242, 1)",
  "rgba(0, 172, 105, 1)",
  "rgba(88, 0, 232, 1)",
  "rgba(244, 161, 0, 1)",
  "rgba(232, 21, 0, 1)",
  "rgba(227, 0, 89, 1)"
];

const defaultChartHoverBackgroundColors = [
  "rgba(0, 97, 242, 0.9)",
  "rgba(0, 172, 105, 0.9)",
  "rgba(88, 0, 232, 0.9)",
  "rgba(244, 161, 0, 0.9)",
  "rgba(232, 21, 0, 0.9)",
  "rgba(227, 0, 89, 0.9)"
];

export default function DashboardPage() {
  const translations = useTranslation();
  const {from, to} = useDateRange();

  const pageTitle = translations.dashboard;
  const pageSubtitle = translations.dashboardPageSubtitle;

  const api = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const costService = useMemo(() => new CostService(api), [api]);
  const incomeService = useMemo(() => new IncomeService(api), [api]);
  const costTypeService = useMemo(() => new CostTypeService(api), [api]);
  const incomeTypeService = useMemo(() => new IncomeTypeService(api), [api]);
  const costCategoryService = useMemo(() => new CostCategoryService(api), [api]);
  const incomeCategoryService = useMemo(() => new IncomeCategoryService(api), [api]);

  const [costTypes, setCostTypes] = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  const [isDefaultIncomeType, setIsDefaultIncomeType] = useState({});

  const getCostTypes = useCallback(async () => {
    const response = await costTypeService.getAll();
    setCostTypes(response.body.map((costType) => costType.name));
  }, [costTypeService]);

  const getIncomeTypes = useCallback(async () => {
    const response = await incomeTypeService.getAll();
    setIncomeTypes(response.body.map((incomeType) => incomeType.name));
    setIsDefaultIncomeType(response.body.reduce((obj, item) => Object.assign(obj, { [item.name]: item.calculate_for_net_2 }), {}));
  }, [incomeTypeService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostTypes(),
        getIncomeTypes()
      ]);
      setIsLoading(false);
    })();
  }, [getCostTypes, getIncomeTypes]);

  const [costCategories, setCostCategories] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);

  const getCostCategories = useCallback(async () => {
    const response = await costCategoryService.getAll();
    setCostCategories(response.body.map((costCategory) => costCategory.name));
  }, [costCategoryService]);

  const getIncomeCategories = useCallback(async () => {
    const response = await incomeCategoryService.getAll();
    setIncomeCategories(response.body.map((incomeCategory) => incomeCategory.name));
  }, [incomeCategoryService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostCategories(),
        getIncomeCategories()
      ]);
      setIsLoading(false);
    })();
  }, [getCostCategories, getIncomeCategories]);

  const [costsData, setCostsData] = useState([]);
  const [incomesData, setIncomesData] = useState([]);

  const getCostsData = useCallback(async (start, end) => {
    const response = await costService.search(start, end);
    setCostsData(
      response.body.map((row) => {
        const dateObject = new Date(row.timestamp);
        const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
          row.currency.symbol
        );
        return {
          id: row.id,
          name: row.name,
          description: row.description,
          type: row.type.name,
          typeId: row.type.id,
          category: row.category.name,
          categoryId: row.category.id,
          amountFormatted: currencyFormatter.format(row.amount),
          amount: row.amount,
          date: DatesUtils.formatDate(dateObject),
          dateFormatted: DatesUtils.format(dateObject),
          currencyId: row.currency.id,
          calculateForGross: row.type.calculate_for_gross,
          calculateForNet1: row.type.calculate_for_net_1,
          calculateForNet2: row.type.calculate_for_net_2
        }
      })
    );
  }, [costService]);

  const getIncomesData = useCallback(async (start, end) => {
    const response = await incomeService.search(start, end);
    setIncomesData(
      response.body.map((row) => {
        const dateObject = new Date(row.timestamp);
        const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
          row.currency.symbol
        );
        return {
          id: row.id,
          name: row.name,
          description: row.description,
          type: row.type.name,
          typeId: row.type.id,
          category: row.category.name,
          categoryId: row.category.id,
          amountFormatted: currencyFormatter.format(row.amount),
          amount: row.amount,
          date: DatesUtils.formatDate(dateObject),
          dateFormatted: DatesUtils.format(dateObject),
          currencyId: row.currency.id,
          calculateForGross: row.type.calculate_for_gross,
          calculateForNet1: row.type.calculate_for_net_1,
          calculateForNet2: row.type.calculate_for_net_2
        }
      })
    );
  }, [incomeService]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getCostsData(from, to),
        getIncomesData(from, to)
      ]);
      setIsLoading(false);
    })();
  }, [from, to, getCostsData, getIncomesData]);

  const totalCosts = useMemo(
    () => {
      const total = costsData.reduce((accumulatedCosts, currentCost) => {
        return accumulatedCosts + currentCost.amount;
      }, 0);

      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        !costsData || costsData.length === 0 ? undefined : costsData[0].currencySymbol
      );

      return currencyFormatter.format(total);
    }, [costsData]);

  const totalIncomes = useMemo(
    () => {
      const total = incomesData.reduce((accumulatedCosts, currentCost) => {
        return accumulatedCosts + currentCost.amount
      }, 0);

      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        !incomesData || incomesData.length === 0 ? undefined : incomesData[0].currencySymbol
      );

      return currencyFormatter.format(total);
    }, [incomesData]);

  const net1 = useMemo(() => {
    const costs = costsData.reduce((accumulatedCosts, currentCost) => {
      if (!currentCost.calculateForNet1) {
        return accumulatedCosts;
      }

      return accumulatedCosts + currentCost.amount;
    }, 0);

    const incomes = incomesData.reduce((accumulatedIncomes, currentIncome) => {
      if (!currentIncome.calculateForNet1) {
        return accumulatedIncomes;
      }

      return accumulatedIncomes + currentIncome.amount;
    }, 0);

    const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
      !costsData || costsData.length === 0 ? undefined : costsData[0].currencySymbol
    );

    return currencyFormatter.format(incomes - costs);
  }, [costsData, incomesData]);

  const net2 = useMemo(() => {
    const costs = costsData.reduce((accumulatedCosts, currentCost) => {
      if (!currentCost.calculateForNet2) {
        return accumulatedCosts;
      }

      return accumulatedCosts + currentCost.amount;
    }, 0);

    const incomes = incomesData.reduce((accumulatedIncomes, currentIncome) => {
      if (!currentIncome.calculateForNet2) {
        return accumulatedIncomes;
      }

      return accumulatedIncomes + currentIncome.amount;
    }, 0);

    const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
      !costsData || costsData.length === 0 ? undefined : costsData[0].currencySymbol
    );

    return currencyFormatter.format(incomes - costs);
  }, [costsData, incomesData]);

  const [costsGroupedData, setCostsGroupedData] = useState({});
  const [incomesGroupedData, setIncomesGroupedData] = useState({});

  const [costsChartData, setCostsChartData] = useState({
    labels: [],
    datasets: [{
      data: []
    }]
  });
  const [incomesChartData, setIncomesChartData] = useState({
    labels: [],
    datasets: [{
      data: []
    }]
  });

  const [costsGroupedByCategoryData, setCostsGroupedByCategoryData] = useState({});
  const [incomesGroupedByCategoryData, setIncomesGroupedByCategoryData] = useState({});

  const [costsChartByCategoryData, setCostsChartByCategoryData] = useState({
    labels: [],
    datasets: [{
      data: []
    }]
  });
  const [incomesChartByCategoryData, setIncomesChartByCategoryData] = useState({
    labels: [],
    datasets: [{
      data: []
    }]
  });

  const getChartLabels = (groupedEntries, groupingOptions = []) => {
    if (!groupingOptions || groupingOptions.length === 0) {
      return Object.keys(groupedEntries);
    }

    return Object.keys(groupedEntries)
      .sort((entryA, entryB) => {
        const indexOfEntryA = (groupingOptions.indexOf(entryA) !== -1 ? groupingOptions.indexOf(entryA) : Infinity);
        const indexOfEntryB = (groupingOptions.indexOf(entryB) !== -1 ? groupingOptions.indexOf(entryB) : Infinity);
        return indexOfEntryA - indexOfEntryB;
      });

    // return Object.keys(groupedEntries)
    //   .reduce((accumulator, key) => {
    //     return [...accumulator, {
    //       key: key,
    //       value: groupedEntries[key]
    //     }];
    //   }, [])
    //   .sort((a, b) => b.value - a.value)
    //   .map(x => x.key);
  };

  const getChartData = (groupedEntries, groupingOptions = []) => {
    if (!groupingOptions || groupingOptions.length === 0) {
      return Object.values(groupedEntries);
    }

    return Object.keys(groupedEntries)
      .map(key => {
        return {
          key: key,
          value: groupedEntries[key]
        };
      })
      .sort((entryA, entryB) => {
        const indexOfEntryA = (groupingOptions.indexOf(entryA.key) !== -1 ? groupingOptions.indexOf(entryA.key) : Infinity);
        const indexOfEntryB = (groupingOptions.indexOf(entryB.key) !== -1 ? groupingOptions.indexOf(entryB.key) : Infinity);
        return indexOfEntryA - indexOfEntryB;
      })
      .map(({value}) => value);
  };

  const getChartBackgroundColors = (chartLabels, allLabels = []) => {
    const string2HexCodeColor = new String2HexCodeColor();

    if (!allLabels || allLabels.length === 0) {
      return chartLabels.map((label, index) => {
        return defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(label);
      });
    }

    return chartLabels
      .sort((entryA, entryB) => {
        const indexOfEntryA = (allLabels.indexOf(entryA) !== -1 ? allLabels.indexOf(entryA) : Infinity);
        const indexOfEntryB = (allLabels.indexOf(entryB) !== -1 ? allLabels.indexOf(entryB) : Infinity);
        return indexOfEntryA - indexOfEntryB;
      })
      .map((label) => {
        const index = allLabels.indexOf(label);
        return defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(label);
      });
  };

  const getChartHoverBackgroundColors = (chartLabels, allLabels = []) => {
    const string2HexCodeColor = new String2HexCodeColor(0.1);

    if (!allLabels || allLabels.length === 0) {
      return chartLabels.map((label, index) => {
        return defaultChartHoverBackgroundColors[index] ?? string2HexCodeColor.stringToColor(label);
      });
    }

    return chartLabels
      .sort((entryA, entryB) => {
        const indexOfEntryA = (allLabels.indexOf(entryA) !== -1 ? allLabels.indexOf(entryA) : Infinity);
        const indexOfEntryB = (allLabels.indexOf(entryB) !== -1 ? allLabels.indexOf(entryB) : Infinity);
        return indexOfEntryA - indexOfEntryB;
      })
      .map((label) => {
        const index = allLabels.indexOf(label);
        return defaultChartHoverBackgroundColors[index] ?? string2HexCodeColor.stringToColor(label);
      });
  };

  useEffect(() => {
    const costsGroupedByType = costsData.reduce((groupedCosts, cost) => {
      const {type} = cost;
      groupedCosts[type] = groupedCosts[type] ?? 0;
      groupedCosts[type] += cost.amount;
      return groupedCosts;
    }, {});

    const costsGroupedByCategory = costsData.reduce((groupedCosts, cost) => {
      const {category} = cost;
      groupedCosts[category] = groupedCosts[category] ?? 0;
      groupedCosts[category] += cost.amount;
      return groupedCosts;
    }, {});

    setCostsGroupedData(costsGroupedByType);
    setCostsGroupedByCategoryData(costsGroupedByCategory);

    setCostsChartData({
      labels: getChartLabels(costsGroupedByType, costTypes),
      datasets: [{
        data: getChartData(costsGroupedByType, costTypes),
        backgroundColor: getChartBackgroundColors(Object.keys(costsGroupedByType), costTypes),
        hoverBackgroundColor: getChartHoverBackgroundColors(Object.keys(costsGroupedByType), costTypes),
        hoverBorderColor: "rgba(234, 236, 244, 1)"
      }]
    });

    setCostsChartByCategoryData({
      labels: getChartLabels(costsGroupedByCategory, costCategories),
      datasets: [{
        data: getChartData(costsGroupedByCategory, costCategories),
        backgroundColor: getChartBackgroundColors(Object.keys(costsGroupedByCategory), costCategories),
        hoverBackgroundColor: getChartHoverBackgroundColors(Object.keys(costsGroupedByCategory), costCategories),
        hoverBorderColor: "rgba(234, 236, 244, 1)"
      }]
    });
  }, [costsData, costTypes, costCategories]);

  useEffect(() => {
    const incomesGroupedByType = incomesData.reduce((groupedIncomes, income) => {
      const {type} = income;
      groupedIncomes[type] = groupedIncomes[type] ?? 0;
      groupedIncomes[type] += income.amount;
      return groupedIncomes;
    }, {});

    const incomesGroupedByCategory = incomesData.reduce((groupedIncomes, income) => {
      const {category} = income;
      groupedIncomes[category] = groupedIncomes[category] ?? 0;
      groupedIncomes[category] += income.amount;
      return groupedIncomes;
    }, {});

    setIncomesGroupedData(incomesGroupedByType);
    setIncomesGroupedByCategoryData(incomesGroupedByCategory);

    setIncomesChartData({
      labels: getChartLabels(incomesGroupedByType, incomeTypes),
      datasets: [{
        data: getChartData(incomesGroupedByType, incomeTypes),
        backgroundColor: getChartBackgroundColors(Object.keys(incomesGroupedByType), incomeTypes),
        hoverBackgroundColor: getChartHoverBackgroundColors(Object.keys(incomesGroupedByType), incomeTypes),
        hoverBorderColor: "rgba(234, 236, 244, 1)"
      }]
    });
    setIncomesChartByCategoryData({
      labels: getChartLabels(incomesGroupedByCategory, incomeCategories),
      datasets: [{
        data: getChartData(incomesGroupedByCategory, incomeCategories),
        backgroundColor: getChartBackgroundColors(Object.keys(incomesGroupedByCategory), incomeCategories),
        hoverBackgroundColor: getChartHoverBackgroundColors(Object.keys(incomesGroupedByCategory), incomeCategories),
        hoverBorderColor: "rgba(234, 236, 244, 1)"
      }]
    });
  }, [incomesData, incomeTypes, incomeCategories]);

  const getDistributionData = (entries, groupingOptions, groupedEntries) => {
    const total = entries.reduce((accumulatedEntries, currentEntry) => {
      return accumulatedEntries + currentEntry.amount;
    }, 0);

    const string2HexCodeColor = new String2HexCodeColor();
    // const entriesOrdering = Object.keys(groupedEntries);
    // const entriesOrdering = Object.keys(groupedEntries)
    //   .reduce((accumulator, key) => {
    //     return [...accumulator, {
    //       key: key,
    //       value: groupedEntries[key]
    //     }];
    //   }, [])
    //   .sort((a, b) => b.value - a.value)
    //   .map(x => x.key);

    const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
      !entries || entries.length === 0 ? undefined : entries[0].currencySymbol
    );

    return groupingOptions
      // .sort((entryA, entryB) => {
      //   const indexOfEntryA = (entriesOrdering.indexOf(entryA) !== -1 ? entriesOrdering.indexOf(entryA) : Infinity);
      //   const indexOfEntryB = (entriesOrdering.indexOf(entryB) !== -1 ? entriesOrdering.indexOf(entryB) : Infinity);
      //   return indexOfEntryA - indexOfEntryB;
      // })
      .map((groupingOption, index) => {
        return {
          label: groupingOption,
          color: defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(groupingOption),
          value: groupedEntries[groupingOption] ? (groupedEntries[groupingOption] / total * 100).toFixed(2) : 0,
          nominalValue: currencyFormatter.format(groupedEntries[groupingOption] ?? 0)
        };
      });
  };

  const getCostsDistributionData = (costs, incomes, groupingOptions, groupedCosts) => {
    const totalCosts = costs.reduce((accumulatedCosts, currentCostEntry) => {
      return accumulatedCosts + currentCostEntry.amount;
    }, 0);

    const totalIncomes = incomes.reduce((accumulatedIncomes, currentIncomeEntry) => {
      return accumulatedIncomes + currentIncomeEntry.amount;
    }, 0);

    const string2HexCodeColor = new String2HexCodeColor();
    const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
      !costs || costs.length === 0 ? undefined : costs[0].currencySymbol
    );

    return groupingOptions
      .map((groupingOption, index) => {
        return {
          label: groupingOption,
          color: defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(groupingOption),
          percentageOfCost: (groupedCosts[groupingOption] && totalCosts) ? (groupedCosts[groupingOption] / totalCosts * 100).toFixed(2) : 0,
          percentageOfIncome: (groupedCosts[groupingOption] && totalIncomes) ? (groupedCosts[groupingOption] / totalIncomes * 100).toFixed(2) : 0,
          nominalValue: currencyFormatter.format(groupedCosts[groupingOption] ?? 0)
        };
      });
  };

  const getIncomesDistributionData = (incomes, groupingOptions, groupedIncomes, isDefaultIncomeType) => {
    const total = incomes.reduce((accumulatedIncomes, currentIncomeEntry) => {
      return accumulatedIncomes + currentIncomeEntry.amount;
    }, 0);

    const string2HexCodeColor = new String2HexCodeColor();
    const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
      !incomes || incomes.length === 0 ? undefined : incomes[0].currencySymbol
    );

    return groupingOptions
      .map((groupingOption, index) => {
        return {
          label: groupingOption,
          color: defaultChartBackgroundColors[index] ?? string2HexCodeColor.stringToColor(groupingOption),
          percentageOfIncome: groupedIncomes[groupingOption] ? (groupedIncomes[groupingOption] / total * 100).toFixed(2) : 0,
          nominalValue: currencyFormatter.format(groupedIncomes[groupingOption] ?? 0),
          isDefault: isDefaultIncomeType[groupingOption] ?? true,
        };
      });
  };

  // const costsByTypeDistributionData = useMemo(() => {
  //   return getDistributionData(costsData, costTypes, costsGroupedData);
  // }, [costsData, costTypes, costsGroupedData]);

  const costsByTypeDistributionData = useMemo(() => {
    return getCostsDistributionData(costsData, incomesData, costTypes, costsGroupedData);
  }, [costsData, incomesData, costTypes, costsGroupedData]);

  // const incomesByTypeDistributionData = useMemo(() => {
  //   return getDistributionData(incomesData, incomeTypes, incomesGroupedData);
  // }, [incomesData, incomeTypes, incomesGroupedData]);

  const incomesByTypeDistributionData = useMemo(() => {
    return getIncomesDistributionData(incomesData, incomeTypes, incomesGroupedData, isDefaultIncomeType);
  }, [incomesData, incomeTypes, incomesGroupedData, isDefaultIncomeType]);

  // const costsByCategoryDistributionData = useMemo(() => {
  //   return getDistributionData(costsData, costCategories, costsGroupedByCategoryData);
  // }, [costsData, costCategories, costsGroupedByCategoryData]);

  const costsByCategoryDistributionData = useMemo(() => {
    return getCostsDistributionData(costsData, incomesData, costCategories, costsGroupedByCategoryData);
  }, [costsData, incomesData, costCategories, costsGroupedByCategoryData]);

  // const incomesByCategoryDistributionData = useMemo(() => {
  //   return getDistributionData(incomesData, incomeCategories, incomesGroupedByCategoryData);
  // }, [incomesData, incomeCategories, incomesGroupedByCategoryData]);

  const incomesByCategoryDistributionData = useMemo(() => {
    return getIncomesDistributionData(incomesData, incomeCategories, incomesGroupedByCategoryData, isDefaultIncomeType);
  }, [incomesData, incomeCategories, incomesGroupedByCategoryData, isDefaultIncomeType]);

  return (
    <DefaultLayout icon={<Activity/>}
                   title={pageTitle}
                   subtitle={pageSubtitle}>
      <ProtectedContent
        allowedRoles={["Admin", "Moderator"]}
        alt={<div style={{textAlign: "center", margin: 10}}>
          <h1>{translations.permissionsAlert}</h1></div>}>
        <Container fluid="xl" className="px-4 mt-n10">
          <Card className="mb-4">
            <Card.Header>
              <Container fluid>
                <Row>
                  <Col style={{paddingLeft: 0, paddingRight: 0}}>
                    <MonthNavigator/>
                  </Col>
                  <Col sm="auto"></Col>
                  <Col style={{paddingLeft: 0, paddingRight: 0}}>
                    <div className="p-2 d-flex gap-3 flex-wrap"
                         style={{float: "right"}}>
                      {StringUtils.format(
                        translations.reviewForPeriod,
                        DatesUtils.format(from),
                        DatesUtils.format(to)
                      )}
                    </div>
                  </Col>
                </Row>
              </Container>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-lg-6 col-xl-3 mb-4">
                  <div className="card bg-success text-white h-100">
                    <div className="card-body">
                      <div
                        className="d-flex justify-content-between align-items-center">
                        <div className="me-3">
                          <div className="text-white-75 small">
                            {translations.totalIncomes}
                          </div>
                          <div className="text-lg fw-bold">
                            {isLoading ? <Spinner size="sm"/> : totalIncomes}
                          </div>
                        </div>
                        <TrendingUp className="feather-xl text-white-50"/>
                      </div>
                    </div>
                    <div
                      className="card-footer d-flex align-items-center justify-content-between small">
                      <Link as={NavLink} to="/incomes"
                            className="text-white stretched-link">
                        {translations.viewIncomes}
                      </Link>
                      <div className="text-white">
                        <FontAwesomeIcon icon="angle-right"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3 mb-4">
                  <div className="card bg-danger text-white h-100">
                    <div className="card-body">
                      <div
                        className="d-flex justify-content-between align-items-center">
                        <div className="me-3">
                          <div className="text-white-75 small">
                            {translations.totalCosts}
                          </div>
                          <div className="text-lg fw-bold">
                            {isLoading ? <Spinner size="sm"/> : totalCosts}
                          </div>
                        </div>
                        <TrendingDown className="feather-xl text-white-50"/>
                      </div>
                    </div>
                    <div
                      className="card-footer d-flex align-items-center justify-content-between small">
                      <Link as={NavLink} to="/costs"
                            className="text-white stretched-link">
                        {translations.viewCosts}
                      </Link>
                      <div className="text-white">
                        <FontAwesomeIcon icon="angle-right"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3 mb-4">
                  <div className="card bg-primary text-white h-100">
                    <div className="card-body">
                      <div
                        className="d-flex justify-content-between align-items-center">
                        <div className="me-3">
                          <div className="text-white-75 small">
                            {translations.net1}
                          </div>
                          <div className="text-lg fw-bold">
                            {isLoading ? <Spinner size="sm"/> : net1}
                          </div>
                        </div>
                        <DollarSign className="feather-xl text-white-50"/>
                      </div>
                    </div>
                    <div
                      className="card-footer d-flex align-items-center justify-content-between small">
                      <Link as={NavLink} to="/reports/quarterly-report"
                            className="text-white stretched-link">
                        {translations.viewReport}
                      </Link>
                      <div className="text-white">
                        <FontAwesomeIcon icon="angle-right"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-xl-3 mb-4">
                  <div className="card bg-primary text-white h-100">
                    <div className="card-body">
                      <div
                        className="d-flex justify-content-between align-items-center">
                        <div className="me-3">
                          <div className="text-white-75 small">
                            {translations.net2}
                          </div>
                          <div className="text-lg fw-bold">
                            {isLoading ? <Spinner size="sm"/> : net2}
                          </div>
                        </div>
                        <DollarSign className="feather-xl text-white-50"/>
                      </div>
                    </div>
                    <div
                      className="card-footer d-flex align-items-center justify-content-between small">
                      <Link as={NavLink} to="/reports/quarterly-report"
                            className="text-white stretched-link">
                        {translations.viewReport}
                      </Link>
                      <div className="text-white">
                        <FontAwesomeIcon icon="angle-right"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xl-6 mb-4">
                  <div className="card card-header-actions h-100">
                    <div className="card-header">
                      {translations.incomesDistributionByType}
                    </div>
                    <div className="card-body">
                      {isLoading ? <Spinner/> : <><DoughnutChart
                        chartData={incomesChartData}/>
                        {/*<div className="list-group list-group-flush">*/}
                        {/*  {incomesByTypeDistributionData.map(({*/}
                        {/*                                        label,*/}
                        {/*                                        color,*/}
                        {/*                                        value*/}
                        {/*                                      }) => (*/}
                        {/*    <div*/}
                        {/*      key={label}*/}
                        {/*      className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">*/}
                        {/*      <div className="me-3">*/}
                        {/*        <FontAwesomeIcon icon="circle"*/}
                        {/*                         className="fa-sm me-1"*/}
                        {/*                         color={color}/>*/}
                        {/*        {label}*/}
                        {/*      </div>*/}
                        {/*      <div className="fw-500 text-dark">{value}%</div>*/}
                        {/*    </div>*/}
                        {/*  ))}*/}
                        {/*</div>*/}
                        <IncomesDistributionList
                          sourceType={translations.type}
                          data={incomesByTypeDistributionData}
                        />
                      </>}
                    </div>
                  </div>
                </div>
                <div className="col-xl-6 mb-4">
                  <div className="card h-100">
                    <div className="card-header">
                      {translations.costsDistributionByType}
                    </div>
                    <div className="card-body">
                      {isLoading ? <Spinner/> : <><DoughnutChart
                        chartData={costsChartData}/>
                        {/*<div className="list-group list-group-flush">*/}
                        {/*  {costsByTypeDistributionData.map(({*/}
                        {/*                                      label,*/}
                        {/*                                      color,*/}
                        {/*                                      value*/}
                        {/*                                    }) => (*/}
                        {/*    <div*/}
                        {/*      key={label}*/}
                        {/*      className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">*/}
                        {/*      <div className="me-3">*/}
                        {/*        <FontAwesomeIcon icon="circle"*/}
                        {/*                         className="fa-sm me-1"*/}
                        {/*                         color={color}/>*/}
                        {/*        {label}*/}
                        {/*      </div>*/}
                        {/*      <div className="fw-500 text-dark">{value}%</div>*/}
                        {/*    </div>*/}
                        {/*  ))}*/}
                        {/*</div>*/}
                        <CostsDistributionList
                          sourceType={translations.type}
                          data={costsByTypeDistributionData}
                        />
                      </>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xl-6 mb-4">
                  <div className="card card-header-actions h-100">
                    <div className="card-header">
                      {translations.incomesDistributionByCategory}
                    </div>
                    <div className="card-body">
                      {isLoading ? <Spinner/> : <><DoughnutChart
                        chartData={incomesChartByCategoryData}/>
                        {/*<div className="list-group list-group-flush">*/}
                        {/*  {incomesByCategoryDistributionData.map(({*/}
                        {/*                                            label,*/}
                        {/*                                            color,*/}
                        {/*                                            value*/}
                        {/*                                          }) => (*/}
                        {/*    <div*/}
                        {/*      key={label}*/}
                        {/*      className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">*/}
                        {/*      <div className="me-3">*/}
                        {/*        <FontAwesomeIcon icon="circle"*/}
                        {/*                         className="fa-sm me-1"*/}
                        {/*                         color={color}/>*/}
                        {/*        {label}*/}
                        {/*      </div>*/}
                        {/*      <div className="fw-500 text-dark">{value}%</div>*/}
                        {/*    </div>*/}
                        {/*  ))}*/}
                        {/*</div>*/}
                        <IncomesDistributionList
                          sourceType={translations.category}
                          data={incomesByCategoryDistributionData}
                        />
                      </>}
                    </div>
                  </div>
                </div>
                <div className="col-xl-6 mb-4">
                  <div className="card h-100">
                    <div className="card-header">
                      {translations.costsDistributionByCategory}
                    </div>
                    <div className="card-body">
                      {isLoading ? <Spinner/> : <><DoughnutChart
                        chartData={costsChartByCategoryData}/>
                        {/*<div className="list-group list-group-flush">*/}
                        {/*  {costsByCategoryDistributionData.map(({*/}
                        {/*                                          label,*/}
                        {/*                                          color,*/}
                        {/*                                          value*/}
                        {/*                                        }) => (*/}
                        {/*    <div*/}
                        {/*      key={label}*/}
                        {/*      className="list-group-item d-flex align-items-center justify-content-between small px-0 py-2">*/}
                        {/*      <div className="me-3">*/}
                        {/*        <FontAwesomeIcon icon="circle"*/}
                        {/*                         className="fa-sm me-1"*/}
                        {/*                         color={color}/>*/}
                        {/*        {label}*/}
                        {/*      </div>*/}
                        {/*      <div className="fw-500 text-dark">{value}%</div>*/}
                        {/*    </div>*/}
                        {/*  ))}*/}
                        {/*</div>*/}
                        <CostsDistributionList
                          sourceType={translations.category}
                          data={costsByCategoryDistributionData}
                        />
                      </>}
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </ProtectedContent>
    </DefaultLayout>
  );
}
